/**
 * Agent kill switch: shared data layer (client + hooks + schemas).
 *
 * Blocks all of an agent's tool calls at IAP verify, timed or indefinite, and
 * lifts the block. Reused by the AIQ Graph agent panel and the Agent registry
 * detail (features can't import each other, so this lives in @shared).
 *
 * Endpoints: GET /iap/agent-kills, POST /iap/agent/:agentId/kill,
 * POST /iap/agent/:agentId/unkill. The kill endpoints resolve the org from the
 * caller, so we pin the selected org with an x-organization-id header.
 */
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@shared/api/client';

/* ── Schemas ─────────────────────────────────────────────────────────────── */

export const AgentKillSchema = z.object({
  id: z.string(),
  agentId: z.string(),
  reason: z.string().nullable().optional(),
  createdBy: z.string().nullable().optional(),
  expiresAt: z.string().nullable().optional(),
  createdAt: z.string().optional(),
});
export type AgentKill = z.infer<typeof AgentKillSchema>;

export const AgentKillsResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(AgentKillSchema),
});

export const KillAgentResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
  id: z.string().optional(),
  agentId: z.string().optional(),
  expiresAt: z.string().nullable().optional(),
});

export const UnkillAgentResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
  cleared: z.number().optional(),
});

/* ── Client ──────────────────────────────────────────────────────────────── */

function orgHeader(orgId: string): Record<string, string> {
  return { 'x-organization-id': orgId };
}

export async function listAgentKills(orgId: string): Promise<AgentKill[]> {
  const raw = await apiClient.get<unknown>('/iap/agent-kills', { headers: orgHeader(orgId) });
  return AgentKillsResponseSchema.parse(raw).data;
}

/** Kill an agent: block its tool calls for `durationSeconds` (undefined = indefinite). */
export async function killAgent(
  agentId: string,
  durationSeconds: number | undefined,
  orgId: string
): Promise<{ success: boolean; expiresAt?: string | null }> {
  const raw = await apiClient.post<unknown>(
    `/iap/agent/${agentId}/kill`,
    { durationSeconds, reason: 'Killed via ArmorIQ' },
    { headers: orgHeader(orgId) }
  );
  return KillAgentResponseSchema.parse(raw);
}

/** Lift any active kill on an agent. */
export async function unkillAgent(
  agentId: string,
  orgId: string
): Promise<{ success: boolean; cleared?: number }> {
  const raw = await apiClient.post<unknown>(
    `/iap/agent/${agentId}/unkill`,
    {},
    { headers: orgHeader(orgId) }
  );
  return UnkillAgentResponseSchema.parse(raw);
}

/* ── Hooks ───────────────────────────────────────────────────────────────── */

export const killKeys = {
  all: (orgId: string) => ['agent-kills', { orgId }] as const,
};

/**
 * Active agent kills for the org.
 *
 * The kill trigger + banner mount on every agent detail view (header-level
 * E-stop), so this query is alive far more often than a kill is. Poll fast
 * (15s) only while a kill is live: that keeps the countdown honest against
 * backend auto-expiry; idle at 60s otherwise. Kills fired from THIS client
 * appear instantly via the mutations' invalidateQueries; the slow tick only
 * bounds how late another client's kill can show up.
 */
export function useAgentKills(orgId: string) {
  return useQuery({
    queryKey: killKeys.all(orgId),
    queryFn: () => listAgentKills(orgId),
    enabled: !!orgId,
    refetchInterval: (query) => ((query.state.data?.length ?? 0) > 0 ? 15_000 : 60_000),
  });
}

export function useKillAgent(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, durationSeconds }: { agentId: string; durationSeconds?: number }) =>
      killAgent(agentId, durationSeconds, orgId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: killKeys.all(orgId) });
    },
  });
}

export function useUnkillAgent(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (agentId: string) => unkillAgent(agentId, orgId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: killKeys.all(orgId) });
    },
  });
}
